import React, { useState, useEffect } from 'react';
import { FileText, User, DollarSign, Download, Calendar, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { consultantsAPI } from '@/api/api';
import { formatDate } from '../../utils/dateTimeUtils';

const ConsultantLedger = () => {
  const [consultants, setConsultants] = useState([]);
  const [selectedConsultant, setSelectedConsultant] = useState('');
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConsultants();
  }, []);

  const fetchConsultants = async () => {
    try {
      const response = await consultantsAPI.getConsultants();
      setConsultants(response.data);
    } catch (error) {
      console.error('Error fetching consultants:', error);
    }
  };

  const handleConsultantChange = async (consultantId) => {
    setSelectedConsultant(consultantId);
    if (!consultantId) {
      setLedgerData(null);
      return;
    }

    setLoading(true);
    try {
      const [ledgerResponse, payoutResponse] = await Promise.all([
        consultantsAPI.getConsultantLedger(consultantId),
        consultantsAPI.getStudentWisePayoutReport(consultantId)
      ]);
      
      setLedgerData({
        summary: ledgerResponse.data,
        students: payoutResponse.data.students || [],
        payments: ledgerResponse.data.payments || [],
        commissions: ledgerResponse.data.commissions || []
      });
    } catch (error) {
      console.error('Error fetching ledger:', error);
      alert('Failed to fetch ledger data');
    } finally {
      setLoading(false);
    }
  };

  const exportLedger = () => {
    if (!ledgerData || !ledgerData.students) return;

    const consultant = consultants.find(c => c.id === selectedConsultant);
    const headers = [
      'Type',
      'Date',
      'Student/Payment Details',
      'Commission',
      'Payment',
      'Balance',
      'Status'
    ];

    // Combine commissions and payments with running balance
    let runningBalance = 0;
    const transactions = [];

    // Add all commissions
    ledgerData.students.forEach(student => {
      runningBalance += student.payout_amount;
      transactions.push([
        'Commission',
        formatDate(student.admission_date),
        `${student.student_name} (${student.student_number})`,
        student.payout_amount,
        '',
        runningBalance,
        student.payout_status
      ]);
    });

    // Add all payments
    ledgerData.payments.forEach(payment => {
      runningBalance -= payment.amount;
      transactions.push([
        'Payment',
        payment.payment_date,
        payment.receipt_no,
        '',
        payment.amount,
        runningBalance,
        'Paid'
      ]);
    });

    const csvContent = [
      headers.join(','),
      ...transactions.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ledger_${consultant?.name}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const selectedConsultantData = consultants.find(c => c.id === selectedConsultant);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Consultant Ledger</h1>
          <p className="text-sm text-gray-500 mt-1">Complete transaction history and balance</p>
        </div>
        {ledgerData && (
          <Button
            onClick={exportLedger}
            className="bg-green-600 hover:bg-green-700"
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        )}
      </div>

      {/* Consultant Selection */}
      <div className="bg-white rounded-lg shadow p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Consultant
        </label>
        <select
          value={selectedConsultant}
          onChange={(e) => handleConsultantChange(e.target.value)}
          className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">-- Select Consultant --</option>
          {consultants.map((consultant) => (
            <option key={consultant.id} value={consultant.id}>
              {consultant.name} ({consultant.email})
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading ledger...</p>
        </div>
      ) : ledgerData ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Consultant</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">{selectedConsultantData?.name}</p>
                </div>
                <User className="text-blue-600" size={32} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Commission</p>
                  <p className="text-xl font-bold text-green-600 mt-1">Rs. {(ledgerData.summary?.total_commission || 0).toLocaleString('en-IN')}</p>
                </div>
                <DollarSign className="text-green-600" size={32} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Paid</p>
                  <p className="text-xl font-bold text-blue-600 mt-1">Rs. {(ledgerData.summary?.total_paid || 0).toLocaleString('en-IN')}</p>
                </div>
                <CheckCircle className="text-blue-600" size={32} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Balance Due</p>
                  <p className="text-xl font-bold text-orange-600 mt-1">Rs. {(ledgerData.summary?.balance_due || 0).toLocaleString('en-IN')}</p>
                </div>
                <Clock className="text-orange-600" size={32} />
              </div>
            </div>
          </div>

          {/* Commission Records */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Commission Records</h2>
              <p className="text-sm text-gray-500 mt-1">{ledgerData.students.length} student(s)</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course Fee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {ledgerData.students.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500">No commission records</td>
                    </tr>
                  ) : (
                    ledgerData.students.map((student, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(student.admission_date)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{student.student_name}</div>
                          <div className="text-sm text-gray-500">{student.student_number}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{student.course_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">Rs. {(student.total_course_fee || 0).toLocaleString('en-IN')}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-green-600">Rs. {(student.payout_amount || 0).toLocaleString('en-IN')}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            student.payout_status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {student.payout_status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-right text-sm font-medium text-gray-900">Total Commission:</td>
                    <td className="px-6 py-4 text-sm font-bold text-green-600">Rs. {(ledgerData.summary?.total_commission || 0).toLocaleString('en-IN')}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Payment Records */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Payment History</h2>
              <p className="text-sm text-gray-500 mt-1">{ledgerData.payments.length} payment(s) made</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt No</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Mode</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {ledgerData.payments.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">No payments made yet</td>
                    </tr>
                  ) : (
                    ledgerData.payments.map((payment, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{payment.payment_date}</td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{payment.receipt_no}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{payment.payment_mode}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{payment.reference_number || 'N/A'}</td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-blue-600">Rs. {(payment.amount || 0).toLocaleString('en-IN')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-right text-sm font-medium text-gray-900">Total Paid:</td>
                    <td className="px-6 py-4 text-right text-sm font-bold text-blue-600">Rs. {(ledgerData.summary?.total_paid || 0).toLocaleString('en-IN')}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Balance Summary */}
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg shadow p-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-orange-800 font-medium">Outstanding Balance</p>
                <p className="text-3xl font-bold text-orange-900 mt-2">Rs. {(ledgerData.summary?.balance_due || 0).toLocaleString('en-IN')}</p>
                <p className="text-sm text-orange-700 mt-1">
                  {ledgerData.students.filter(s => s.payout_status === 'Pending').length} student(s) with pending payout
                </p>
              </div>
              <div className="text-right">
                <div className="text-sm text-orange-800">Total Students: {ledgerData.summary?.total_students || 0}</div>
                <div className="text-sm text-orange-800 mt-1">Commission Rate: Course-wise payout</div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <p>Select a consultant to view ledger details</p>
        </div>
      )}
    </div>
  );
};

export default ConsultantLedger;
